import { z } from "zod";

export const whapiThreadSchema = z.object({
  id: z.number(),
  group: z.number(),
  order: z.object({
    id: z.number(),
    source: z.string(),
    status: z.string(),
    restaurant_name: z.string(),
    external_po_number: z.string().nullish(),
    internal_po_number: z.string(),
    order_datetime: z.string(),
    expected_delivery_date: z.string().nullable(),
    restaurant_branch: z.string().nullish(),
    restaurant_address: z.string(),
    order_details: z.string(),
    branch_address: z.string(),
    supplier: z.number(),
    created_by: z.number(),
  }),
  content: z.object({
    messages: z.array(
      z.object({
        id: z.union([z.string(), z.number()]).optional(),
        from: z.string().optional(),
        text: z.object({ body: z.string() }).optional(),
        type: z.string().optional(),
        source: z.string().optional(),
        chat_id: z.string().optional(),
        from_me: z.boolean().optional(),
        from_name: z.string().nullable().optional(),
        timestamp: z.number().optional(),
        multimedia_id: z.number().optional(),
      }),
    ),
  }),
  processed: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.number(),
});

export type WhapiThread = z.infer<typeof whapiThreadSchema>;

export const whapiThreadsSchema = z.array(whapiThreadSchema);

export type WhapiThreads = z.infer<typeof whapiThreadsSchema>;

export const whapiGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  is_active: z.boolean(),
  contacts_count: z.number(),
  primary_order_taker: z.string().nullable(),
  messages: z.array(
    z
      .object({
        id: z.number().optional(),
        meta_id: z.number().optional(),
        created_at: z.string(),
        content: z
          .array(
            z.object({
              text: z.object({ body: z.string() }).optional(),
              document: z.object({ body: z.string() }).optional(),
              image: z.object({ body: z.string() }).optional(),
              type: z.string(),
              multimedia_id: z.number().optional(),
              timestamp: z.number(),
              from_name: z.string().nullable(),
              from: z.string(),
              from_me: z.boolean().nullable().optional(),
              id: z.number().optional(),
            }),
          )
          .nullable(),
        llm_response: z.string().nullable(),
        order: z.union([z.string(), z.number()]).optional(),
      })
      .optional(),
  ),
  contacts: z.array(
    z.object({
      phone_number: z.string(),
      name: z.string().nullable(),
    }),
  ),
});

export type WhapiGroup = z.infer<typeof whapiGroupSchema>;

export const whapiGroupsSchema = z.array(whapiGroupSchema);

export type WhapiGroups = z.infer<typeof whapiGroupsSchema>;

export const whapiGroupActivationSchema = z.object({
  id: z.number(),
  name: z.string(),
  is_active: z.boolean(),
  updated_at: z.string(),
});

export type WhapiGroupActivation = z.infer<typeof whapiGroupActivationSchema>;

export const whapiChannelSchema = z.object({
  id: z.number(),
  channel_id: z.string(),
  phone_number: z.string(),
  supplier_user: z.number(),
  is_enabled: z.boolean(),
  is_universal: z.boolean(),
  qr_code_base64: z.string().nullable(),
  auth_status: z.enum(["AUTH", "QR_FETCHED", "FAILED", "CREATED"]),
  created_at: z.string(),
  updated_at: z.string(),
});

export type WhapiChannel = z.infer<typeof whapiChannelSchema>;

// Consolidated schema for WhatsApp Connection Status (Authenticated or QR)
export const whapiConnectionStatusSchema = z.object({
  start_at: z.number(),
  uptime: z.number(),
  status: z.object({
    code: z.number(),
    text: z.enum(["AUTH", "QR"]), // Enum for status text
  }),
  version: z.string(),
  user: z
    .object({
      id: z.string(),
      name: z.string(),
      saved: z.boolean(),
      is_business: z.boolean(),
      profile_pic: z.string().url().optional(),
    })
    .nullable(), // User is nullable (present for AUTH, null for QR)
  device_id: z.number().optional(), // device_id is only present for AUTH
  ip: z.string(),
  is_business: z.boolean(), // This seems duplicated, present at root and in user. Keeping at root.
  channel_id: z.string(),
});

// Type derived from the consolidated schema
export type WhapiConnectionStatus = z.infer<typeof whapiConnectionStatusSchema>;

// Define specific types based on the status text if needed for type guards
export type WhapiAuthenticatedStatus = Omit<WhapiConnectionStatus, "user"> & {
  status: { code: number; text: "AUTH" };
  user: NonNullable<WhapiConnectionStatus["user"]>;
  device_id: number;
};

export type WhapiQRStatus = Omit<WhapiConnectionStatus, "user"> & {
  status: { code: number; text: "QR" };
  user: null;
  device_id?: undefined; // Explicitly undefined for QR status
};

// Base metadata fields common to all multimedia types
const baseMetadataSchema = z.object({
  id: z.string(),
  mime_type: z.string(),
  file_size: z.number(),
  sha256: z.string(),
});

// Voice/Audio metadata
const voiceMetadataSchema = baseMetadataSchema.extend({
  seconds: z.number(),
});

// Image metadata
const imageMetadataSchema = baseMetadataSchema.extend({
  width: z.number(),
  height: z.number(),
});

// Document metadata
const documentMetadataSchema = baseMetadataSchema.extend({
  caption: z.string().optional(),
  filename: z.string(),
  page_count: z.number().optional(),
});

// Union type for metadata based on multimedia_type
const multimediaMetadataSchema = z.union([
  voiceMetadataSchema,
  imageMetadataSchema,
  documentMetadataSchema,
  baseMetadataSchema, // fallback for video/audio
]);

export const whapiMultimediaMessageSchema = z.object({
  id: z.number(),
  message_id: z.string(),
  multimedia_type: z.enum(["voice", "image", "video", "audio", "document"]),
  whatsapp_timestamp: z.number().nullable(),
  file: z.string().nullable(),
  transcription: z.string().nullable(),
  metadata: multimediaMetadataSchema,
  processed: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  group: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable(),
  contact: z
    .object({
      id: z.number(),
      name: z.string().nullable(),
      phone_number: z.string(),
    })
    .nullable(),
});

export type WhapiMultimediaMessage = z.infer<
  typeof whapiMultimediaMessageSchema
>;
